/**
 * useTrackingPlatforms - Plataformas de rastreamento (Gestão Rastreador) via RPC.
 *
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial: listagem paginada e criação/edição.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useTrackingPlatforms');

// Preserva o `this` do cliente (RPC fora da tipagem gerada)
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

export interface TrackingPlatform {
  id: string;
  name: string;
  supplier_person_id: string | null;
  supplier_name: string | null;
  access_url: string | null;
  is_active: boolean;
}

export interface TrackingPlatformFilters {
  search: string;
  status: 'active' | 'inactive' | 'all';
  page: number;
  pageSize: number;
}

/** Listagem paginada (também usada pelo modal de homologação). */
export function useTrackingPlatforms(f: TrackingPlatformFilters, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['stock-tracking-platforms', tenantId, f],
    enabled: !!tenantId && enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await rpc('get_stock_tracking_platforms', {
        p_search: f.search || null,
        p_status: f.status,
        p_page: f.page,
        p_page_size: f.pageSize,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar plataformas', { error: error.message });
        throw new Error(error.message);
      }
      const p = (data || {}) as { total?: number; items?: TrackingPlatform[] };
      return { total: p.total ?? 0, items: p.items ?? [] };
    },
  });
}

export interface TrackingPlatformInput {
  name: string;
  supplier_person_id: string | null;
  access_url: string | null;
  is_active: boolean;
}

/** Cria ou edita uma plataforma (validação definitiva no servidor). */
export function useUpsertTrackingPlatform() {
  const { currentTenant } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string | null; data: TrackingPlatformInput }) => {
      const { data: res, error } = await rpc('upsert_stock_tracking_platform', {
        p_data: data,
        p_platform_id: id ?? null,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw new Error(error.message);
      return res as string;
    },
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['stock-tracking-platforms'] });
      toast.success(v.id ? 'Plataforma atualizada' : 'Plataforma criada');
    },
    onError: (e: Error) => {
      logger.error('Erro ao salvar plataforma', { error: e.message });
      toast.error(e.message || 'Não foi possível salvar a plataforma');
    },
  });
}

/** URL aceita: vazia ou http/https sem espaços/aspas (espelha o servidor). */
export function isValidPlatformUrl(url: string): boolean {
  const v = url.trim();
  return v === '' || (v.length <= 500 && /^https?:\/\/[^\s<>"']+$/i.test(v));
}
